<template>
  <div class="dashboard-page">
    <Navbar />
    <div class="app-container">
      <Sidebar />

      <div id="main-content" class="main-content" :class="{ 'mobile-content': isMobile }" tabindex="-1">
        <a
          id="accesskey-c"
          href="#accesskey-c"
          accesskey="C"
          class="accesskey-brick sr-only-focusable"
          title="主要內容區塊，快捷鍵：Alt+C"
          aria-label="主要內容區塊"
        >:::</a>
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
        </div>
        <div v-else-if="certificateList" class="dashboard-content">
          <div class="welcome-section">
            <h1>{{ t("certificate.title") }}</h1>
            <p>{{ t("certificate.subtitle") }}</p>
          </div>

          <div class="section-container courses-table-container">
            <div
              class="table-wrapper table-scroll-container"
              ref="tableWrapper"
            >
              <a-table
                ref="tableRef"
                :dataSource="certificateList.certs"
                :columns="columns"
                :pagination="false"
                :rowKey="(record) => record.id"
                class="course-table"
                :locale="{
                  emptyText: t('certificate.emptyText'),
                }"
                :customRow="
                  (row) => ({
                    class: { 'selected-row': false },
                  })
                "
                @change="handleTableChange"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.dataIndex === 'name'">
                    <span class="course-title">{{ record.name }}</span>
                  </template>

                  <template v-else-if="column.dataIndex === 'finished_at'">
                    <span>{{ record.finished_at }}</span>
                  </template>

                  <template v-else-if="column.dataIndex === 'download'">
                    <a-button
                      type="link"
                      class="download-btn"
                      @click="downloadCertificate(record)"
                      :loading="record.isDownloading"
                    >
                      <DownloadOutlined />
                    </a-button>
                  </template>
                </template>
              </a-table>
            </div>

            <div class="pagination-container" v-if="certificateList.total > 0">
              <div class="pagination-info">
                {{ (currentPage - 1) * certificateList.perPage + 1 }}-{{
                  Math.min(
                    currentPage * certificateList.perPage,
                    certificateList.total
                  )
                }}
                {{ t("certificate.of") }} {{ certificateList.total }}
              </div>

              <div class="pagination-spacer"></div>

              <a-pagination
                v-model:current="currentPage"
                :total="certificateList.total"
                :pageSize="certificateList.perPage"
                @change="handlePageChange"
                show-less-items
                size="small"
              />
            </div>
          </div>
        </div>

        <div v-else class="error-container">
          <p>{{ t("certificate.userInfoError") }}</p>
          <a-button type="primary" @click="redirectToLogin">{{
            t("common.loginAgain")
          }}</a-button>
        </div>
      </div>
    </div>
    <FooterSection />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, h } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Navbar from "../components/Navbar.vue";
import Sidebar from "../components/Sidebar.vue";
import FooterSection from "../components/FooterSection.vue";
import { message } from "ant-design-vue";

import {
  CheckCircleOutlined,
  FileTextOutlined,
  TrophyOutlined,
  RightOutlined,
  AppstoreOutlined,
  CarryOutOutlined,
  CheckSquareOutlined,
  BookOutlined,
  UserOutlined,
  DownOutlined,
  HomeOutlined,
  MenuOutlined,
  CloseOutlined,
  ReadOutlined,
  LogoutOutlined,
  EllipsisOutlined,
  DownloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons-vue";
import { certificatesAPI } from "../services/api";

const router = useRouter();
const { t, locale } = useI18n();
const user = ref(null);
const loading = ref(true);

// RWD 相關狀態
const isMobile = ref(window.innerWidth <= 768);
const mobileMenuOpen = ref(false);

// 證書列表相關狀態
const certificateList = ref({
  certs: [],
  total: 0,
  page: 1,
  perPage: 10,
});
const currentPage = ref(1);

// 表格相關
const tableRef = ref(null);

// 表格列定義
const columns = [
  {
    title: t("certificate.certificateName"),
    dataIndex: "name",
    key: "name",
    width: "60%",
  },
  {
    title: t("certificate.completionDate"),
    dataIndex: "finished_at",
    key: "finished_at",
    width: "20%",
  },
  {
    title: t("certificate.download"),
    dataIndex: "download",
    key: "download",
    width: "20%",
  },
];

const handleTableChange = (pagination, filters, sorter) => {
  // 移除排序邏輯，因為 API 已經排序
};

// 簡化版下載證書函數
const downloadCertificate = async (record) => {
  const certificateId = record?.id;

  if (!certificateId) {
    message.error(t("certificate.invalidDownloadLink"));
    return;
  }

  const loadingMessage = message.loading(t("certificate.preparingDownload"), 0);

  try {
    const response = await certificatesAPI.download(certificateId);
    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);
    const filename = `${
      record.certificate_number || `certificate-${certificateId}`
    }.pdf`;

    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    loadingMessage();
    message.success(t("certificate.downloadSuccess"));
  } catch (error) {
    console.error("下載證書失敗:", error);
    loadingMessage();

    if (error.response) {
      if (error.response.status === 404) {
        message.error(t("certificate.certificateNotFound"));
      } else if (
        error.response.status === 401 ||
        error.response.status === 403
      ) {
        message.error(t("certificate.downloadPermissionDenied"));
      } else {
        message.error(t("certificate.downloadFailed"));
      }
    } else {
      message.error(t("certificate.downloadNetworkError"));
    }
  } finally {
    setTimeout(() => {
      try {
        loadingMessage();
      } catch (e) {
        // 忽略任何關閉提示時的錯誤
      }
    }, 3000);
  }
};

// 獲取證書列表
const fetchCertificates = async (page = 1) => {
  try {
    loading.value = true;
    currentPage.value = page;
    const response = await certificatesAPI.list();
    const items = response.data || [];

    const start = (page - 1) * certificateList.value.perPage;
    const paged = items.slice(start, start + certificateList.value.perPage);

    certificateList.value = {
      ...certificateList.value,
      certs: paged.map((item) => ({
        id: item.id,
        certificate_number: item.certificate_number,
        name: item.course_title,
        finished_at: item.finish_date,
      })),
      total: items.length,
      page,
    };
  } catch (error) {
    console.error("獲取證書列表失敗:", error);
    const apiMessage = error?.response?.data?.message;
    message.error(apiMessage || t("certificate.fetchCertificateListError"));
  } finally {
    loading.value = false;
  }
};

// 自定義滾動器方法

// 處理分頁變更
const handlePageChange = (page) => {
  currentPage.value = page;
  fetchCertificates(page);
};

onMounted(() => {
  loading.value = true;

  // 併行獲取用戶資訊和證書列表
  Promise.all([fetchCertificates(currentPage.value)])
    .catch((error) => {
      console.error("初始化資料失敗:", error);
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  background: #f9f9f9;
  padding-top: 58px;
}

/* 整體應用容器 */
.app-container {
  display: flex;
  min-height: calc(100vh - 58px);
}

/* 右側主內容區 */
.main-content {
  flex: 1;
  margin-left: 220px;
  padding: 36px 16px 56px;
  background-color: transparent;
  min-height: calc(100vh - 58px);
  overflow-y: auto;
}

/* 其他樣式保持不變 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.dashboard-content {
  max-width: 950px;
  margin: 30px auto;
  display: flex;
  flex-direction: column;
}

/* 歡迎區塊樣式 */
.welcome-section {
  margin-bottom: 24px;
}

.welcome-section h1 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
}

.welcome-section p {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
}

/* RWD 樣式 */
@media (max-width: 768px) {
  .app-container {
    display: block;
  }

  .welcome-section h1 {
    font-size: 20px;
  }

  .welcome-section p {
    font-size: 14px;
  }
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  color: #999;
  font-size: 14px;
}

.pagination-spacer {
  flex-grow: 1;
}

.courses-table-container {
  background: #fff;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* 下載按鈕樣式 */
.download-btn {
  color: var(--color-primary);
  font-size: 16px;
}

/* 表頭樣式 */
:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  background-color: #ffffff !important; /* 強制所有表頭背景為白色 */
  font-weight: 600;
  color: #333;
  padding: 12px 16px;
}

:deep(.ant-table-wrapper td.ant-table-column-sort) {
  background-color: transparent !important; /* 修正排序列的單元格背景色 */
}

:deep(.ant-table-wrapper .ant-table-column-sort) {
  background-color: #ffffff !important; /* 強制排序列標題的背景為白色 */
}

/* 響應式調整 */
@media (max-width: 768px) {
  /* 手機版表格調整 */
  :deep(.ant-table) {
    width: 100%;
    overflow-x: auto;
  }

  .main-content {
    margin-left: 0;
    padding: 76px 16px 20px;
  }

  :deep(.ant-table-thead > tr > th),
  :deep(.ant-table-tbody > tr > td) {
    white-space: nowrap;
    padding: 8px 12px;
  }

  .course-title {
    font-size: 14px;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 空數據顯示樣式 */
.empty-courses {
  padding: 40px 0;
  text-align: center;
}

.empty-courses-content {
  max-width: 360px;
  margin: 0 auto;
}

.empty-courses-text {
  font-size: 15px;
  color: #666;
  margin-bottom: 24px;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background-color: rgba(2, 115, 112, 0.08) !important;
}

/* 強制設置已選中行的背景色 */
:deep(.ant-table-row-selected > td) {
  background-color: rgba(2, 115, 112, 0.08) !important;
}

/* 確保三點選單顯示正確 */
.ant-dropdown-link {
  cursor: pointer;
  color: #999;
}

.ant-dropdown-link:hover {
  color: var(--color-primary);
}

/* 確保下載按鈕樣式正確 */
.download-btn {
  padding: 0;
  margin: 0;
}

/* 表格包裝器樣式 */
.table-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
}

/* 確保表格有最小寬度以觸發滾動 */
.table-wrapper :deep(.ant-table) {
  min-width: 500px;
}

.table-wrapper :deep(.ant-table-thead > tr > th),
.table-wrapper :deep(.ant-table-tbody > tr > td) {
  white-space: nowrap;
  width: auto;
}

/* 表格橫向滾動樣式 */
.table-scroll-container {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  position: relative;
}

.table-scroll-container::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  box-sizing: border-box;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: #696969;
    pointer-events: none;

    svg {
      width: 20px !important;
      height: 20px !important;
      display: block;
    }
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    outline: none;
    padding: 0 12px 0 38px;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    box-sizing: border-box;
    &::placeholder {
      color: #696969;
    }
  }
}

.custom-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 140px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;

  .select-label {
    font-size: 16px !important;
    color: #1a1a1a;
    white-space: nowrap;
    user-select: none;
  }

  .select-divider {
    margin: 0 8px;
    color: #b4b4b4;
    font-size: 16px !important;
    user-select: none;
  }

  select.select-input {
    flex: 1;
    height: 100%;
    border: none;
    background: transparent !important;
    outline: none;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    cursor: pointer;
    padding-right: 20px;
    appearance: none;
    -webkit-appearance: none;

    option {
      color: #1a1a1a;
      background: #ffffff;
      font-size: 16px !important;
    }
  }

  .select-arrow {
    position: absolute;
    right: 12px;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
}
</style>
